import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { offlineDb, type MutationQueueItem } from './db'
import { beginSync, endSync, reportSynced } from './syncStatus'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import { patchCachedEntityId, patchCachedRowVersion } from './patchCachedRowVersion'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { SyncResponse } from '../DTOs/Sync/SyncDtos'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { recordSyncFailure, uploadSyncFailures } from './syncFailures'
import { isOnlineNow, subscribeToReachability } from './connectivity'
import { refreshMachineLists } from './refreshMachineLists'
import { SYNC_ENTITIES, buildSyncRequest, childReferences, inApplyOrder, invalidateStale } from './syncEntities'

// Note: a 401 says the session could not be renewed, not that the change was wrong. The queue survives
// sign-out, so leaving the change queued lets it sync once the user signs back in.
function isSessionFailure(code?: number) {
  return code === 401
}

const SERVER_ERROR_RETRY_MS = 7 * 24 * 60 * 60 * 1000

// Note: 0 is no reply at all, the network gone or the request timed out. It goes through keep like any other
// failure, so an outage is logged once per change instead of showing only as a queue that will not drain.
const NO_REPLY = 0
const NO_REPLY_MESSAGE = 'No reply from the server: the network dropped or the request timed out.'

const SERVER_BUSY = 503

function isServerError(code?: number) {
  return code !== undefined && code >= 500
}

type SyncAttemptMode = 'batch' | 'single'

// Note: a change to a row whose create is still queued waits for that create to sync and swap its temp id for
// the real one. Sent in the same request it would still carry the temp id, which the server cannot find.
function waitsForCreate(item: MutationQueueItem, queue: MutationQueueItem[]): boolean {
  return item.operation !== 'create'
    && typeof item.entityId === 'number'
    && item.entityId < 0
    && queue.some(other => other.operation === 'create' && other.entity === item.entity && other.entityId === item.entityId)
}

async function waitsForParent(item: MutationQueueItem): Promise<boolean> {
  const data = item.data as Record<string, unknown> | undefined
  if (!data) return false

  for (const [parent, children] of childReferences()) {
    for (const child of children) {
      const parentId = data[child.field]
      if (child.entity !== item.entity || typeof parentId !== 'number' || parentId >= 0) continue
      if (await offlineDb.mutationQueue.where('[entity+entityId]').equals([parent, parentId]).count() > 0) return true
    }
  }
  return false
}

async function remapQueuedChildren(entity: MutationQueueItem['entity'], tempId: number, realId: number): Promise<void> {
  for (const child of SYNC_ENTITIES[entity]?.children ?? []) {
    const queued = await offlineDb.mutationQueue.where('entity').equals(child.entity).toArray()

    for (const item of queued) {
      const data = item.data as Record<string, unknown> | undefined
      if (!data || data[child.field] !== tempId) continue
      await offlineDb.mutationQueue.update(item.id!, { data: { ...data, [child.field]: realId } })
    }
  }
}

async function remapQueuedEntityId(
  entity: MutationQueueItem['entity'],
  tempId: number,
  realId: number,
): Promise<void> {
  const queued = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, tempId])
    .toArray()

  for (const item of queued) {
    const data = item.data && typeof item.data === 'object'
      ? { ...(item.data as Record<string, unknown>) }
      : item.data

    const idField = SYNC_ENTITIES[entity]?.idField
    if (idField && data && typeof data === 'object' && (data as Record<string, unknown>)[idField] === tempId)
      (data as Record<string, unknown>)[idField] = realId

    await offlineDb.mutationQueue.update(item.id!, { entityId: realId, data })
  }
}

async function refreshStaleQueries(queryClient: QueryClient, staleEntities: Set<MutationQueueItem['entity']>) {
  try {
    for (const entity of staleEntities) {
      await refreshMachineLists(queryClient, entity)
      await invalidateStale(queryClient, entity)
    }
  } catch (error) {
    console.error('Refreshing after a sync failed', error)
  }
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      const queue = await offlineDb.mutationQueue.orderBy('createdAt').toArray()
      const snapshot = queue.filter(item => !waitsForCreate(item, queue))

      if (snapshot.length) beginSync(snapshot.length)

      if (navigator.onLine
        && store.getState().shift.provisionalShiftDate
        && !queue.some(item => item.entity === 'completeShift')) {
        store.dispatch(clearProvisionalShift())
      }

      if (snapshot.length === 0) return

      const staleEntities = new Set<MutationQueueItem['entity']>()

      const processResults = async (results: SyncResponse['results'], items: MutationQueueItem[], mode: SyncAttemptMode) => {
        reportSynced(results.length)
        const byClientId = new Map(items.map(i => [i.clientId, i]))
        const retried: { item: MutationQueueItem; message?: string }[] = []
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          for (const result of results) {
            const item = byClientId.get(result.clientId)
            if (!item) continue
            if (result.status === SyncStatus.Retry) {
              retried.push({ item, message: typeof result.serverState === 'string' ? result.serverState : undefined })
              continue
            }
            staleEntities.add(item.entity)

            if (item.operation === 'create'
              && result.status === SyncStatus.Applied
              && typeof result.serverId === 'number'
              && typeof item.entityId === 'number'
              && item.entityId < 0) {
              await remapQueuedChildren(item.entity, item.entityId, result.serverId)
              await remapQueuedEntityId(item.entity, item.entityId, result.serverId)
              patchCachedEntityId(item.entity, item.entityId, result.serverId)
            }

            if (item.operation === 'update' && result.status === SyncStatus.Applied) {
              patchCachedRowVersion(item.entity, item.entityId, result.serverRowVersion)
            }
            if (result.status !== SyncStatus.Applied) {
              await offlineDb.syncIssues.add({
                clientId: result.clientId,
                entity: item.entity,
                operation: item.operation,
                status: result.status,
                entityId: item.entityId,
                shiftId: item.shiftId,
                serverState: result.serverState,
                clientData: item.data,
                createdAt: Date.now(),
              })
            }
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        })
        for (const { item, message } of retried) await keep(item, SERVER_BUSY, message, mode)
      }

      const quarantine = async (item: MutationQueueItem, code?: number, reason?: string) => {
        staleEntities.add(item.entity)
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          await offlineDb.syncIssues.add({
            clientId: item.clientId,
            entity: item.entity,
            operation: item.operation,
            status: SyncStatus.Error,
            entityId: item.entityId,
            shiftId: item.shiftId,
            serverState: reason ?? `The server rejected this change${code ? ` (HTTP ${code})` : ''}, so it could not be applied. It has been removed from the queue - please re-enter it.`,
            clientData: item.data,
            createdAt: Date.now(),
          })
          await offlineDb.mutationQueue.where('clientId').equals(item.clientId).delete()
        })
      }

      // Note: a kept change is retried every 15 seconds, so it is logged when its failure changes,
      // not on every retry.
      const keep = async (item: MutationQueueItem, code: number | undefined, message: string | undefined, mode: SyncAttemptMode) => {
        const serverErrorSince = isServerError(code) ? item.serverErrorSince ?? Date.now() : undefined
        if (item.lastFailureCode === code && item.serverErrorSince === serverErrorSince) return
        await offlineDb.mutationQueue.update(item.id!, { lastFailureCode: code, serverErrorSince })
        await recordSyncFailure({ ...item, serverErrorSince }, code, message, 'Kept', { mode, batchSize: snapshot.length })
      }

      const handleFailure = async (item: MutationQueueItem, errors: ActionResponse<unknown>['errors'], mode: SyncAttemptMode) => {
        const code = errors?.[0]?.code
        const message = errors?.[0]?.description
        if (isSessionFailure(code)) {
          await keep(item, code, message, mode)
          return 'stop'
        }
        if (isServerError(code) && Date.now() - (item.serverErrorSince ?? Date.now()) < SERVER_ERROR_RETRY_MS) {
          await keep(item, code, message, mode)
          return 'continue'
        }
        const reason = isServerError(code)
          ? `The server kept failing (HTTP ${code}) for 7 days, so this change could not be applied. It has been removed from the queue - please re-enter it.`
          : undefined
        await quarantine(item, code, reason)
        await recordSyncFailure(item, code, message, 'Removed', { mode, batchSize: snapshot.length })
        return 'continue'
      }

      try {
        const response = await syncAjax.sync(buildSyncRequest(snapshot)).catch(() => null)
        if (response === null) {
          for (const item of snapshot) await keep(item, NO_REPLY, NO_REPLY_MESSAGE, 'batch')
          return
        }
        if (response.success && response.data) {
          await processResults(response.data.results, snapshot, 'batch')
        } else if (isSessionFailure(response.errors?.[0]?.code)) {
          for (const item of snapshot) await keep(item, 401, response.errors?.[0]?.description, 'batch')
          return
        } else if (snapshot.length === 1) {
          await handleFailure(snapshot[0], response.errors, 'batch')
        } else {
          for (const item of inApplyOrder(snapshot)) {
            const current = await offlineDb.mutationQueue.get(item.id!)
            if (!current || await waitsForParent(current)) continue
            const single = await syncAjax.sync(buildSyncRequest([current])).catch(() => null)
            if (single === null) {
              await keep(current, NO_REPLY, NO_REPLY_MESSAGE, 'single')
              return
            }
            if (single.success && single.data) {
              await processResults(single.data.results, [current], 'single')
            } else if (await handleFailure(current, single.errors, 'single') === 'stop') {
              return
            }
          }
        }
      } catch {
        return
      }

      // Note: not awaited, so the lock is released first. These refetches retry, and pause while the screen is
      // hidden, so awaiting them held the lock long after the changes themselves had synced.
      void refreshStaleQueries(queryClient, staleEntities)
    } finally {
      isSyncingRef.current = false
      endSync()
      void uploadSyncFailures()
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && isOnlineNow()) runSync() }

    trigger()
    const unsubscribe = subscribeToReachability(trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    const retry = pendingCount > 0 ? setInterval(trigger, 15000) : null

    return () => {
      active = false
      unsubscribe()
      if (retry) clearInterval(retry)
    }
  }, [connection, runSync, pendingCount])
}
